import type { AdGroupDetailResponse } from "@adpilot/shared";
import { IconArrowRight, IconDatabaseOff, IconPackage } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CartesianGrid, Line, LineChart } from "recharts";
import {
  NegativesGroup,
  SearchTermsTable,
  TargetsSection,
} from "@/components/campaign/targeting-tables";
import { DataGridBody, DataGridHeader, DataGridRow } from "@/components/patterns/DataTable";
import { PageTabs } from "@/components/patterns/PageTabs";
import { ReportingFilters } from "@/components/patterns/reporting-filters";
import { SearchInput } from "@/components/patterns/SearchInput";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { TabsContent } from "@/components/ui/tabs";
import { fetchAdGroupDetail } from "@/lib/campaign-api";
import { useFormatters } from "@/lib/format";
import { requireModule } from "@/lib/module-access";
import { cn } from "@/lib/utils";
import { useReportingPeriodStore } from "@/stores/reporting-period";
import { useReportingTimeZoneStore } from "@/stores/reporting-time-zone";

export const Route = createFileRoute("/_authed/campaigns/$id/ad-groups/$adGroupId")({
  beforeLoad: () => {
    requireModule("campaigns");
  },
  component: AdGroupDetailPage,
});

function AdGroupDetailPage() {
  const { t } = useTranslation("campaign");
  const { id, adGroupId } = Route.useParams();
  const range = useReportingPeriodStore((state) => state.period);
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  const query = useQuery({
    queryKey: ["campaign", id, "ad-groups", adGroupId, range, timeZone],
    queryFn: () => fetchAdGroupDetail(id, adGroupId, range, timeZone),
  });

  if (query.isLoading) return <AdGroupDetailSkeleton />;
  if (query.isError || !query.data) {
    return (
      <AdGroupFrame>
        <ErrorState message={t("adGroupDetail.loadError")} onRetry={() => void query.refetch()} />
      </AdGroupFrame>
    );
  }
  if (!query.data.connected || !query.data.adGroup) {
    return (
      <AdGroupFrame>
        <EmptyState
          icon={<IconDatabaseOff className="size-16" />}
          title={t("adGroupDetail.unavailableTitle")}
          description={t("adGroupDetail.unavailableDescription")}
        />
      </AdGroupFrame>
    );
  }
  return <AdGroupDetailContent data={query.data} campaignId={id} />;
}

export function AdGroupDetailContent({
  data,
  campaignId,
}: {
  data: AdGroupDetailResponse;
  campaignId: string;
}) {
  const { t } = useTranslation("campaign");
  const { targets, negatives, searchTerms, advertisedProducts } = data;
  return (
    <AdGroupFrame>
      <AdGroupHeader data={data} />
      <AdGroupSummary data={data} />
      <PageTabs
        defaultValue="overview"
        density="compact"
        items={[
          { value: "overview", label: t("adGroup.overview") },
          {
            value: "products",
            label: t("adGroupDetail.tabs.products"),
            count: advertisedProducts.length,
          },
          { value: "targets", label: t("targets.title"), count: targets.length },
          { value: "search-terms", label: t("searchTerms.title"), count: searchTerms.length },
          { value: "negatives", label: t("negatives.title"), count: negatives.length },
        ]}
      >
        <TabsContent value="overview">
          <Overview data={data} />
        </TabsContent>
        <TabsContent value="products">
          <AdvertisedProducts data={data} />
        </TabsContent>
        <TabsContent value="targets">
          <TargetsSection campaignId={campaignId} targets={targets} />
        </TabsContent>
        <TabsContent value="search-terms">
          <SearchTermsTable searchTerms={searchTerms} />
        </TabsContent>
        <TabsContent value="negatives">
          <AdGroupNegatives campaignId={campaignId} negatives={negatives} />
        </TabsContent>
      </PageTabs>
    </AdGroupFrame>
  );
}

function AdGroupFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-full min-w-0 flex-1 flex-col gap-[14px] px-6 pb-[22px] pt-[18px]">
      {children}
    </div>
  );
}

function AdGroupHeader({ data }: { data: AdGroupDetailResponse }) {
  const { t } = useTranslation("campaign");
  const adGroup = data.adGroup!;
  return (
    <div className="flex min-h-[52px] shrink-0 flex-wrap items-start justify-between gap-x-6 gap-y-3">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Link to="/campaigns">{t("detail.breadcrumb")}</Link>
          <span>/</span>
          <Link
            to="/campaigns/$id"
            params={{ id: adGroup.campaignId }}
            className="truncate hover:text-ink"
          >
            {adGroup.campaignName}
          </Link>
          <span>/</span>
          <span>{t("adGroupDetail.breadcrumb")}</span>
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          <h1 className="truncate text-[24px] font-semibold leading-7 tracking-[-0.02em] text-ink">
            {adGroup.name}
          </h1>
          <span className="rounded-full border border-green/20 bg-green/5 px-2 py-1 text-[9px] font-medium text-green-11">
            {t("adGroupDetail.aiManaged")}
          </span>
        </div>
      </div>
      <ReportingFilters scope="resource" />
    </div>
  );
}

function AdGroupSummary({ data }: { data: AdGroupDetailResponse }) {
  const { t } = useTranslation("campaign");
  const product = data.advertisedProducts[0];
  return (
    <div className="grid h-[104px] shrink-0 grid-cols-[356px_minmax(0,1fr)] gap-[14px]">
      <section className="flex items-center gap-3 overflow-hidden rounded-[14px] border border-line bg-surface px-3.5">
        {product?.imageUrl ? (
          <img src={product.imageUrl} alt="" className="size-14 rounded-[8px] object-cover" />
        ) : (
          <div className="flex size-14 shrink-0 items-center justify-center rounded-[8px] bg-subtle">
            <IconPackage className="size-5 text-muted-foreground" />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-[9px] uppercase text-muted-foreground">
            {t("adGroupDetail.advertisedProduct")}
          </p>
          <p className="mt-1 line-clamp-2 text-[11px] font-semibold leading-4 text-ink">
            {product?.title ?? t("adGroupDetail.productSyncing")}
          </p>
          <p className="mt-1 truncate text-[9px] text-muted-foreground">
            {t("adGroupDetail.asin", { asin: product?.asin ?? "—" })}
          </p>
        </div>
      </section>
      <MetricStrip metrics={data.adGroup!.metrics} />
    </div>
  );
}

function MetricStrip({
  metrics,
}: {
  metrics: AdGroupDetailResponse["adGroup"] extends infer _
    ? NonNullable<AdGroupDetailResponse["adGroup"]>["metrics"]
    : never;
}) {
  const { t } = useTranslation("campaign");
  const { formatCurrency, formatPercent, formatNumber } = useFormatters();
  const items = [
    { label: t("adGroupDetail.metrics.spend"), value: formatCurrency(metrics.spend) },
    { label: t("metrics.sales"), value: formatCurrency(metrics.sales) },
    { label: t("metrics.acos"), value: formatPercent(metrics.acos) },
    { label: t("metrics.orders"), value: formatNumber(metrics.orders) },
  ];
  return (
    <section className="grid h-[104px] grid-cols-4 divide-x overflow-hidden rounded-[14px] border border-line bg-surface">
      {items.map((item) => (
        <div key={item.label} className="flex flex-col justify-center px-4">
          <span className="text-[9px] uppercase text-muted-foreground">{item.label}</span>
          <span className="mt-1 text-[20px] font-semibold tabular-nums text-ink">{item.value}</span>
          <span className="mt-0.5 text-[9px] text-green-11">
            {t("detail.performance.selectedPeriod")}
          </span>
        </div>
      ))}
    </section>
  );
}

function Overview({ data }: { data: AdGroupDetailResponse }) {
  const { t } = useTranslation("campaign");
  const { formatCurrency, formatPercent } = useFormatters();
  const chartConfig = {
    sales: { label: t("metrics.sales"), color: "var(--chart-1)" },
    spend: { label: t("adGroupDetail.metrics.spend"), color: "var(--chart-2)" },
  } satisfies ChartConfig;
  const adGroup = data.adGroup!;
  const counts = new Map<string, number>();
  for (const target of data.targets)
    counts.set(target.targetType, (counts.get(target.targetType) ?? 0) + 1);
  return (
    <div className="flex flex-col gap-[14px]">
      <div className="grid h-[258px] grid-cols-[minmax(0,2.3fr)_minmax(320px,1fr)] gap-[14px]">
        <section className="overflow-hidden rounded-[14px] border border-line bg-surface">
          <div className="flex h-[58px] items-start justify-between px-4 py-3">
            <div>
              <h2 className="text-[14px] font-semibold text-ink">
                {t("adGroupDetail.performanceTrend")}
              </h2>
              <p className="mt-0.5 text-[9px] text-muted-foreground">
                {t("adGroupDetail.trendSubtitle")}
              </p>
            </div>
            <div className="flex items-center gap-4 text-[9px] text-muted-foreground">
              <span>
                <span className="mr-1.5 inline-block size-1.5 rounded-full bg-ink" />
                {t("detail.adGroups.table.adSales")}
              </span>
              <span>
                <span className="mr-1.5 inline-block size-1.5 rounded-full bg-muted-foreground/35" />
                {t("metrics.spend")}
              </span>
            </div>
          </div>
          <ChartContainer
            config={chartConfig}
            className="h-[198px] w-full px-3 pb-3"
            initialDimension={{ width: 680, height: 198 }}
          >
            <LineChart data={data.trend} margin={{ left: 0, right: 14 }}>
              <CartesianGrid vertical={false} stroke="var(--gray-200)" />
              <ChartTooltip content={<ChartTooltipContent indicator="line" />} />
              <Line
                dataKey="sales"
                stroke="var(--color-sales)"
                strokeWidth={2.25}
                dot={false}
                isAnimationActive={false}
              />
              <Line
                dataKey="spend"
                stroke="var(--color-spend)"
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ChartContainer>
        </section>
        <section className="overflow-hidden rounded-[14px] border border-line bg-surface">
          <div className="flex h-[46px] items-center justify-between px-4">
            <h2 className="text-[14px] font-semibold text-ink">{t("adGroupDetail.aiActivity")}</h2>
            <span className="rounded-full border border-green/20 bg-green/5 px-2 py-1 text-[9px] text-green-11">
              {t("detail.adGroups.aiState.managed")}
            </span>
          </div>
          <div className="px-4 text-[10px]">
            <p className="text-muted-foreground">{t("adGroupDetail.latestRunSchedule")}</p>
            <p className="mt-1 text-[11px] font-semibold text-ink">
              {data.bidHistory.length > 0
                ? t("adGroupDetail.bidsAdjusted", { count: data.bidHistory.length })
                : t("adGroupDetail.monitoringGuardrails")}
            </p>
            <p className="mt-1 text-muted-foreground">
              {t("adGroupDetail.acosAcross", { value: formatPercent(adGroup.metrics.acos) })}
            </p>
            <div className="mt-3 grid grid-cols-2 border-t border-line pt-3">
              <div>
                <p className="text-[8px] uppercase text-muted-foreground">
                  {t("adGroupDetail.policyLabel")}
                </p>
                <p className="mt-1 font-semibold">{t("adGroupDetail.inheritedSemiAuto")}</p>
              </div>
              <div>
                <p className="text-[8px] uppercase text-muted-foreground">
                  {t("detail.policy.targetAcos")}
                </p>
                <p className="mt-1 font-semibold">{formatPercent(adGroup.metrics.acos)}</p>
              </div>
            </div>
            <p className="mt-3 text-amber-11">
              {t("adGroupDetail.proposedActionsReview", {
                count: data.targets.filter(
                  (target) => target.risk === "high" || target.risk === "critical",
                ).length,
              })}
            </p>
          </div>
        </section>
      </div>
      <div className="grid min-h-[274px] grid-cols-3 gap-[14px]">
        <section className="rounded-[14px] border border-line bg-surface p-4">
          <h3 className="text-[13px] font-semibold text-ink">{t("adGroupDetail.targetMix")}</h3>
          <div className="mt-4 space-y-3">
            {Array.from(counts)
              .slice(0, 3)
              .map(([kind, value], index) => (
                <div key={kind}>
                  <div className="flex justify-between text-[10px]">
                    <span className="capitalize">{kind.replace("_", " ")}</span>
                    <span className="font-semibold">{value}</span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-subtle">
                    <div
                      className={cn(
                        "h-full rounded-full",
                        index === 0 ? "bg-ink" : "bg-muted-foreground/40",
                      )}
                      style={{
                        width: `${Math.max(8, (value / Math.max(1, data.targets.length)) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
          </div>
        </section>
        <section className="rounded-[14px] border border-line bg-surface p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-[13px] font-semibold text-ink">
              {t("adGroupDetail.advertisedProducts")}
            </h3>
            <span className="text-[9px] text-muted-foreground">
              {t("adGroupDetail.activeCount", { total: data.advertisedProducts.length })}
            </span>
          </div>
          {data.advertisedProducts[0] ? (
            <div className="mt-4 flex gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-[7px] bg-ink text-[8px] text-white">
                ASIN
              </div>
              <div className="min-w-0">
                <p className="truncate text-[11px] font-semibold">
                  {data.advertisedProducts[0].title ?? data.advertisedProducts[0].asin}
                </p>
                <p className="mt-1 text-[9px] text-muted-foreground">
                  {data.advertisedProducts[0].asin}
                </p>
                <p className="mt-1 text-[9px] text-muted-foreground">
                  {t("adGroupDetail.primaryProduct")}
                </p>
              </div>
            </div>
          ) : (
            <p className="mt-4 text-[10px] text-muted-foreground">
              {t("adGroupDetail.productSyncingDot")}
            </p>
          )}
        </section>
        <section className="rounded-[14px] border border-line bg-surface p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-[13px] font-semibold text-ink">
              {t("adGroupDetail.recentBidActivity")}
            </h3>
            <span className="text-[9px] text-muted-foreground">{t("adGroupDetail.viewRun")}</span>
          </div>
          <div className="mt-4 space-y-3">
            {data.bidHistory.slice(0, 3).map((event) => (
              <div key={event.id} className="flex items-center justify-between gap-3 text-[10px]">
                <span className="truncate">{event.targetLabel}</span>
                <span
                  className={
                    event.newBid != null && event.oldBid != null && event.newBid > event.oldBid
                      ? "text-green-11"
                      : "text-red-11"
                  }
                >
                  {event.oldBid == null ? "—" : formatCurrency(event.oldBid)} →{" "}
                  {event.newBid == null ? "—" : formatCurrency(event.newBid)}
                </span>
              </div>
            ))}
            {data.bidHistory.length === 0 ? (
              <p className="text-[10px] leading-4 text-muted-foreground">
                {t("adGroupDetail.noBidChanges")}
              </p>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}

function AdvertisedProducts({ data }: { data: AdGroupDetailResponse }) {
  const { t } = useTranslation("campaign");
  const [query, setQuery] = useState("");
  const { formatCurrency, formatNumber, formatPercent } = useFormatters();
  const products = data.advertisedProducts.filter((product) =>
    `${product.title ?? ""} ${product.asin}`.toLowerCase().includes(query.toLowerCase()),
  );
  return (
    <section className="overflow-hidden rounded-[14px] border border-line bg-surface">
      <div className="flex h-[48px] items-center justify-between border-b border-line px-4">
        <div>
          <h2 className="text-xs font-semibold text-ink">
            {t("adGroupDetail.advertisedProductsCount", {
              total: data.advertisedProducts.length,
            })}
          </h2>
          <p className="mt-0.5 text-[9px] text-muted-foreground">
            {t("adGroupDetail.productsPromoted")}
          </p>
        </div>
        <SearchInput
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("adGroupDetail.searchProductPlaceholder")}
          className="w-[210px]"
        />
      </div>
      <DataGridHeader
        density="standard"
        className="grid-cols-[minmax(260px,2fr)_90px_90px_90px_75px_70px_85px] px-4 text-[10px]"
      >
        <span>{t("adGroupDetail.productsTable.product")}</span>
        <span>{t("adGroup.status")}</span>
        <span>{t("metrics.spend")}</span>
        <span>{t("metrics.sales")}</span>
        <span>{t("metrics.acos")}</span>
        <span>{t("metrics.orders")}</span>
        <span>{t("adGroupDetail.productsTable.salesShare")}</span>
      </DataGridHeader>
      <DataGridBody>
        {products.map((product) => (
          <DataGridRow
            key={product.id}
            density="rich"
            className="grid-cols-[minmax(260px,2fr)_90px_90px_90px_75px_70px_85px] px-4 text-xs"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              {product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  alt=""
                  className="size-[50px] rounded-[6px] object-cover"
                />
              ) : (
                <div className="flex size-[50px] items-center justify-center rounded-[6px] bg-subtle">
                  <IconPackage className="size-4" />
                </div>
              )}
              <div className="min-w-0">
                <p className="truncate font-medium text-ink">{product.title ?? product.asin}</p>
                <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">{product.asin}</p>
              </div>
            </div>
            <span>
              <span className="rounded-full border border-green/20 bg-green/5 px-2 py-1 text-[9px] capitalize text-green-11">
                {product.status}
              </span>
            </span>
            <MetricCell
              value={product.metrics == null ? null : formatCurrency(product.metrics.spend)}
            />
            <MetricCell
              value={product.metrics == null ? null : formatCurrency(product.metrics.sales)}
            />
            <MetricCell
              value={product.metrics == null ? null : formatPercent(product.metrics.acos)}
            />
            <MetricCell
              value={product.metrics == null ? null : formatNumber(product.metrics.orders)}
            />
            <MetricCell
              value={product.metrics == null ? null : formatPercent(product.metrics.salesShare)}
            />
          </DataGridRow>
        ))}
      </DataGridBody>
      <div className="border-t border-line bg-subtle px-4 py-3 text-xs text-muted-foreground">
        {data.advertisedProductCoverage.status === "complete"
          ? t("adGroupDetail.coverage.complete", {
              covered: data.advertisedProductCoverage.coveredDays,
              total: data.advertisedProductCoverage.totalDays,
            })
          : data.advertisedProductCoverage.status === "partial"
            ? t("adGroupDetail.coverage.partial", {
                covered: data.advertisedProductCoverage.coveredDays,
                total: data.advertisedProductCoverage.totalDays,
              })
            : t("adGroupDetail.coverage.unavailable", {
                covered: data.advertisedProductCoverage.coveredDays,
                total: data.advertisedProductCoverage.totalDays,
              })}
      </div>
    </section>
  );
}

function MetricCell({ value }: { value: string | null }) {
  return (
    <span
      className={cn("font-mono text-[10px] tabular-nums", value == null && "text-muted-foreground")}
    >
      {value ?? "—"}
    </span>
  );
}

function AdGroupNegatives({
  campaignId,
  negatives,
}: {
  campaignId: string;
  negatives: AdGroupDetailResponse["negatives"];
}) {
  const { t } = useTranslation("campaign");
  const keywordCount = negatives.filter((negative) => negative.targetType === "keyword").length;
  const productCount = negatives.length - keywordCount;
  const stats: [string, number][] = [
    [t("adGroupDetail.negativeStats.adGroupNegatives"), negatives.length],
    [t("adGroupDetail.negativeStats.negativeKeywords"), keywordCount],
    [t("adGroupDetail.negativeStats.negativeProducts"), productCount],
  ];
  return (
    <div className="flex flex-col gap-[14px]">
      <section className="grid h-[72px] grid-cols-3 divide-x overflow-hidden rounded-[14px] border border-line bg-surface">
        {stats.map(([label, value]) => (
          <div key={label} className="flex flex-col justify-center px-4">
            <span className="text-[9px] uppercase text-muted-foreground">{label}</span>
            <span className="mt-1 text-[18px] font-semibold tabular-nums text-ink">{value}</span>
          </div>
        ))}
      </section>
      {negatives.length === 0 ? (
        <EmptyState
          icon={<IconDatabaseOff className="size-12" />}
          title={t("adGroupDetail.noNegativesTitle")}
          description={t("adGroupDetail.noNegativesDescription")}
        />
      ) : (
        <NegativesGroup
          title={t("adGroupDetail.negativeStats.adGroupNegatives")}
          negatives={negatives}
          showAdGroup={false}
        />
      )}
      <Link
        to="/campaigns/$id"
        params={{ id: campaignId }}
        className="flex min-h-[58px] items-center justify-between rounded-[14px] border border-line bg-surface px-4 text-xs"
      >
        <span>
          <span className="block font-semibold text-ink">
            {t("adGroupDetail.inheritedCampaignNegatives")}
          </span>
          <span className="mt-1 block text-[10px] text-muted-foreground">
            {t("adGroupDetail.inheritedCampaignNegativesHint")}
          </span>
        </span>
        <span className="flex items-center gap-1 font-medium text-ink">
          {t("adGroupDetail.viewCampaign")} <IconArrowRight className="size-3.5" />
        </span>
      </Link>
    </div>
  );
}

function AdGroupDetailSkeleton() {
  return (
    <AdGroupFrame>
      <Skeleton className="h-[92px] rounded-[10px]" />
      <Skeleton className="h-[78px] rounded-[10px]" />
      <Skeleton className="h-[37px] rounded-[8px]" />
      <div className="grid grid-cols-[1.6fr_1fr] gap-4">
        <Skeleton className="h-[290px] rounded-[10px]" />
        <Skeleton className="h-[290px] rounded-[10px]" />
      </div>
    </AdGroupFrame>
  );
}
